<template>
    <a-modal title="对话管理" :visible="visible" :width="1400" :footer="null" @cancel="handleClose" class="dialogue-modal"
        :destroyOnClose="true">
        <div class="dialogue-editor" style="display:flex;height:100%;">
            <!-- 左侧：节点树 -->
            <a-card style="width:400px;margin-right:16px;overflow:auto;height:100%;">
                <div style="display:flex;gap:8px;margin-bottom:16px;">
                    <a-button type="primary" icon="plus" @click="handleAddNode">新建节点</a-button>
                    <a-button icon="reload" @click="refreshDialogueTree">刷新</a-button>
                </div>

                <!-- 节点树 -->
                <a-spin :spinning="loading">
                    <a-tree v-if="treeData.length > 0" :tree-data="treeData"
                        :replace-fields="{ children: 'children', title: 'title', key: 'key' }"
                        :default-expand-all="true" @select="onTreeSelect"
                        :selectedKeys="selectedNode ? [selectedNode.id] : []" style="max-height:650px;overflow:auto">
                        <template slot="title" slot-scope="item">
                            <div :class="{ 'selected-node-tree': selectedNode && selectedNode.id === item.id }"
                                style="display: flex; align-items: center; width:100%;">
                                <span style="flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">
                                    {{ getNodeLabel(item) }}
                                </span>

                                <a-tag v-if="item.startNode" color="green"
                                    style="margin-left: 4px; font-size: 10px;">起始</a-tag>
                                <a-tag v-if="item.nodeType === 'video'" color="blue"
                                    style="margin-left: 4px; font-size: 10px;">视频</a-tag>
                                <a-tag v-if="item.nodeType === 'audio'" color="cyan"
                                    style="margin-left: 4px; font-size: 10px;">音频</a-tag>
                                <a-tag v-if="item.nodeType === 'phone'" color="red"
                                    style="margin-left: 4px; font-size: 10px;">电话</a-tag>
                            </div>
                        </template>
                    </a-tree>

                    <a-empty v-if="!loading && treeData.length === 0" description="暂无对话节点">
                        <a-button type="primary" @click="handleAddNode">创建起始节点</a-button>
                    </a-empty>
                </a-spin>
            </a-card>

            <!-- 右侧：节点编辑 -->
            <a-card style="flex:1;overflow:auto;height:100%;">
                <div v-if="!selectedNode" style="padding:24px;color:#666;text-align:center;">
                    <a-icon type="select" style="font-size:48px;color:#d9d9d9;margin-bottom:16px;" />
                    <div>请选择左侧节点进行编辑</div>
                </div>

                <div v-else>
                    <!-- 节点标题和基本信息 -->
                    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">
                        <h3 style="margin:0;">
                            <a-tag :color="getNodeTypeColor(selectedNode.nodeType)" size="small">
                                {{ getNodeTypeText(selectedNode.nodeType) }}
                            </a-tag>
                            <span style="margin-left:8px;">节点 {{ selectedNode.id }}</span>
                            <a-tag v-if="selectedNode.startNode" color="green" style="margin-left:8px;">起始</a-tag>
                        </h3>

                        <div>
                            <a-button type="link" icon="edit" @click="handleEditNode(selectedNode)">编辑</a-button>
                            <a-button type="link" icon="delete" @click="handleDeleteNode(selectedNode)"
                                style="color:#f5222d;">删除</a-button>
                        </div>
                    </div>

                    <!-- 节点内容预览 -->
                    <a-descriptions bordered :column="{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }" size="small"
                        style="margin-bottom:16px;">
                        <a-descriptions-item label="节点内容" :span="2">
                            <div
                                style="max-height:100px;overflow:auto;background:#fafafa;padding:8px;border-radius:4px;">
                                <div v-if="selectedNode.nodeType === 'text'">
                                    <div v-for="(item, index) in selectedNode.content.split('&')" :key="index"
                                        style="margin-bottom:4px;">
                                        {{ index + 1 }}. {{ item.trim() }}
                                    </div>
                                </div>
                                <div v-else-if="['audio', 'video', 'phone'].includes(selectedNode.nodeType)">
                                    <div style="color:#1890ff;word-break:break-all;">
                                        {{ selectedNode.content || '未上传文件' }}
                                    </div>
                                </div>
                            </div>
                        </a-descriptions-item>

                        <a-descriptions-item label="跳转节点">
                            <div style="display:flex;align-items:center;gap:8px;">
                                <a-select style="min-width:200px;" :value="selectedNode.jumpNodeId" allowClear
                                    placeholder="设置跳转节点" @change="(value) => updateJumpNodeId(value)"
                                    :loading="loading">
                                    <a-select-option v-for="node in allNodes" :key="node.id" :value="node.id"
                                        :disabled="node.id === selectedNode.id">
                                        {{ getNodeLabel(node) }}
                                    </a-select-option>
                                </a-select>

                                <template v-if="selectedNode.jumpNodeId">
                                    <span style="color:#1890ff;margin-left:8px;">
                                        ({{ getNodeLabelById(selectedNode.jumpNodeId) }})
                                    </span>
                                </template>
                                <template v-else>
                                    <a-tag color="red">未设置</a-tag>
                                </template>
                            </div>
                        </a-descriptions-item>
                    </a-descriptions>

                    <!-- 媒体预览（如果是音视频） -->
                    <div v-if="['video', 'phone'].includes(selectedNode.nodeType) && selectedNode.content"
                        style="margin-bottom:16px;">
                        <h4>📹 视频预览</h4>
                        <div style="width:100%;max-height:300px;background:#000;border-radius:4px;overflow:hidden;">
                            <video v-if="selectedNode.content" controls :src="selectedNode.content"
                                style="width:100%;height:auto;max-height:300px;">
                                抱歉，您的浏览器不支持内嵌视频播放。
                            </video>
                        </div>
                    </div>

                    <div v-if="selectedNode.nodeType === 'audio' && selectedNode.content" style="margin-bottom:16px;">
                        <h4>🎵 音频预览</h4>
                        <div style="background:#f5f5f5;padding:16px;border-radius:4px;">
                            <audio v-if="selectedNode.content" controls :src="selectedNode.content" style="width:100%;">
                                抱歉，您的浏览器不支持内嵌音频播放。
                            </audio>
                        </div>
                    </div>

                    <!-- 当前节点的所有子节点 -->
                    <div style="margin-top:24px;">
                        <h4>子节点</h4>
                        <a-list v-if="childNodes.length > 0" bordered :dataSource="childNodes"
                            style="max-height:300px;overflow:auto">
                            <template slot="renderItem" slot-scope="node">
                                <a-list-item>
                                    <div
                                        style="display:flex;justify-content:space-between;align-items:center;width:100%;">
                                        <div>
                                            <a-tag :color="getNodeTypeColor(node.nodeType)" size="small">
                                                {{ getNodeTypeText(node.nodeType) }}
                                            </a-tag>
                                            {{ getNodeLabel(node) }}
                                        </div>
                                        <div>
                                            <a-button type="link" size="small" @click="selectNode(node)">查看</a-button>
                                            <a-button type="link" size="small"
                                                @click="handleEditNode(node)">编辑</a-button>
                                        </div>
                                    </div>
                                </a-list-item>
                            </template>
                        </a-list>
                        <div v-else style="text-align:center;padding:16px;color:#999;">
                            此节点暂无子节点
                        </div>

                        <div style="margin-top:8px;">
                            <a-button type="dashed" icon="plus" @click="handleAddChildNode(selectedNode)">
                                添加子节点
                            </a-button>
                        </div>
                    </div>
                </div>
            </a-card>
        </div>

        <!-- 节点编辑弹窗 -->
        <a-modal :title="nodeModal.title" :visible="nodeModal.visible" :width="800" :confirm-loading="nodeModal.loading"
            @ok="submitNodeForm" @cancel="handleNodeModalCancel" :destroyOnClose="true">
            <a-form-model ref="nodeForm" :model="nodeForm" :rules="nodeRules" :label-col="{ span: 4 }"
                :wrapper-col="{ span: 20 }">
                <a-row :gutter="16">
                    <a-col :span="12">
                        <a-form-model-item label="节点类型" prop="nodeType">
                            <a-select v-model="nodeForm.nodeType" placeholder="请选择节点类型" @change="handleNodeTypeChange">
                                <a-select-option value="text">文本节点</a-select-option>
                                <a-select-option value="audio">音频节点</a-select-option>
                                <a-select-option value="video">视频节点</a-select-option>
                                <a-select-option value="phone">电话节点</a-select-option>
                            </a-select>
                        </a-form-model-item>
                    </a-col>
                </a-row>

                <!-- 内容区域 -->
                <a-form-model-item label="节点内容" prop="content">
                    <div v-if="nodeForm.nodeType === 'text'">
                        <a-textarea v-model="nodeForm.content" placeholder="请输入节点内容，多条内容用 & 分隔" :rows="4"
                            :maxLength="1000" />
                        <div
                            style="margin-top: 8px; padding: 8px; background: #f5f5f5; border-radius: 4px; font-size: 12px; color: #666; line-height: 1.5;">
                            <div>提示：</div>
                            <div>1. 使用 & 符号分隔多条内容</div>
                        </div>

                        <!-- 内容预览 -->
                        <div v-if="nodeForm.content && nodeForm.content.includes('&')"
                            style="margin-top: 12px; padding: 12px; background: #f9f9f9; border-radius: 4px; border-left: 3px solid #1890ff;">
                            <div style="font-weight: bold; margin-bottom: 8px; color: #333;">内容预览：</div>
                            <div v-for="(item, index) in nodeForm.content.split('&')" :key="index"
                                style="display: flex; align-items: center; margin-bottom: 6px;">
                                <a-tag size="small" color="blue">{{ index + 1 }}</a-tag>
                                <span style="margin-left: 8px; color: #666;">{{ item.trim() }}</span>
                            </div>
                        </div>
                    </div>

                    <div v-else>
                        <a-upload :custom-request="handleMediaUpload" :before-upload="beforeMediaUpload"
                            :accept="getMediaAccept()" :show-upload-list="false">
                            <a-button>
                                <a-icon type="upload" />上传文件
                            </a-button>
                            <div slot="tip" style="font-size: 12px; color: #999; margin-top: 8px;">
                                {{ getMediaTip() }}
                            </div>
                        </a-upload>
                        <div v-if="nodeForm.content"
                            style="margin-top: 12px; padding: 10px; background: #f5f5f5; border-radius: 4px; border: 1px dashed #d9d9d9;">
                            <div style="font-weight: bold; margin-bottom: 8px;">当前文件：</div>
                            <div
                                style="margin-bottom: 8px; padding: 8px; background: white; border-radius: 2px; font-size: 12px; word-break: break-all;">
                                {{ nodeForm.content }}
                            </div>
                            <div>
                                <a-button type="link" size="small"
                                    @click="previewMedia(nodeForm.content, nodeForm.nodeType)">预览</a-button>
                                <a-button type="link" size="small" @click="nodeForm.content = ''"
                                    style="color:#f5222d">清空</a-button>
                            </div>
                        </div>
                    </div>
                </a-form-model-item>

                <a-row :gutter="16">
                    <a-col :span="12">
                        <a-form-model-item label="跳转节点">
                            <a-select v-model="nodeForm.jumpNodeId" placeholder="选择跳转节点" allow-clear show-search>
                                <a-select-option v-for="node in allNodes" :key="node.id" :value="node.id"
                                    :disabled="node.id === nodeForm.id">
                                    {{ getNodeLabel(node) }}
                                </a-select-option>
                            </a-select>
                        </a-form-model-item>
                    </a-col>
                    <a-col :span="12">
                        <a-form-model-item label="父节点" v-if="nodeModal.mode === 'add' && selectedNode">
                            <div style="color:#666;">
                                {{ selectedNode.nodeName || getNodeLabel(selectedNode) }}
                                <div style="font-size:12px;color:#999;">将在当前节点下添加子节点</div>
                            </div>
                            <input type="hidden" v-model="nodeForm.parentNodeId" />
                        </a-form-model-item>
                    </a-col>
                </a-row>

                <a-form-model-item label="是否为起始节点" v-if="nodeModal.mode === 'add'">
                    <a-switch v-model="nodeForm.startNode" checked-children="是" un-checked-children="否"
                        :disabled="hasStartNode" />
                    <div v-if="hasStartNode" style="font-size: 12px; color: #faad14; margin-left: 10px;">
                        当前角色已存在起始节点，只能有一个起始节点
                    </div>
                </a-form-model-item>
            </a-form-model>
        </a-modal>

        <!-- 媒体预览弹窗 -->
        <a-modal :visible="previewModal.visible" :title="previewModal.title" :footer="null"
            @cancel="previewModal.visible = false" width="800">
            <div v-if="previewModal.type === 'video'" style="text-align:center;">
                <video :src="previewModal.url" controls style="width:100%;max-height:500px;"></video>
            </div>
            <div v-else-if="previewModal.type === 'audio'" style="text-align:center;">
                <audio :src="previewModal.url" controls style="width:100%;"></audio>
            </div>
        </a-modal>
    </a-modal>
</template>

<script>
import {
    getDialogueTree,
    addDialogueNode,
    updateDialogueNode,
    deleteDialogueNode
} from '@/api/video/dialogueNode'

export default {
    name: 'DialogueManagement',
    props: {
        visible: {
            type: Boolean,
            default: false
        },
        characterId: {
            type: Number,
            required: true
        },
        characterName: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            // 所有节点列表
            allNodes: [],
            // 树形数据
            treeData: [],
            loading: false,

            // 选中的节点
            selectedNode: null,

            // 节点编辑弹窗
            nodeModal: {
                visible: false,
                title: '添加节点',
                loading: false,
                mode: 'add' // add, edit
            },

            // 节点表单
            nodeForm: {
                id: null,
                characterId: null,
                parentNodeId: null,
                nodeType: 'text',
                content: '',
                triggerType: 'click',
                jumpNodeId: null,
                startNode: false
            },

            // 节点表单验证规则
            nodeRules: {
                nodeType: [
                    { required: true, message: '请选择节点类型', trigger: 'change' }
                ],
                content: [
                    { required: true, message: '请输入节点内容', trigger: 'blur' }
                ]
            },

            // 媒体预览弹窗
            previewModal: {
                visible: false,
                title: '',
                url: '',
                type: '' // video, audio
            }
        }
    },
    computed: {
        // 是否已有起始节点
        hasStartNode() {
            return this.allNodes.some(node => node.startNode)
        },

        // 当前节点的子节点
        childNodes() {
            if (!this.selectedNode) return []
            return this.allNodes.filter(node => node.parentNodeId === this.selectedNode.id)
        },

        // 获取所有节点映射
        nodeMap() {
            const map = {}
            this.allNodes.forEach(node => {
                map[node.id] = node
            })
            return map
        }
    },
    watch: {
        visible(val) {
            if (val && this.characterId) {
                this.init()
            }
        }
    },
    methods: {
        // 初始化
        init() {
            this.fetchDialogueTree()
        },

        // 获取对话树
        async fetchDialogueTree() {
            this.loading = true
            try {
                const { data } = await getDialogueTree(this.characterId)
                this.allNodes = data || []
                this.buildTreeData()

                // 自动选择起始节点
                const startNode = this.allNodes.find(n => n.startNode)
                if (startNode) {
                    this.selectNode(startNode)
                }
            } catch (error) {
                console.error('获取对话树失败:', error)
                this.$message.error('获取对话树失败')
            } finally {
                this.loading = false
            }
        },

        // 构建树形数据（参考您提供的代码逻辑）
        buildTreeData() {
            console.log('当前所有节点数据:', this.allNodes);
            if (!this.allNodes || this.allNodes.length === 0) {
                this.treeData = []
                return
            }

            const nodeMap = this.allNodes.reduce((map, node) => {
                map[node.id] = node
                return map
            }, {})

            // 1. 查找起始节点
            const rootNode = this.allNodes.find(n => n.startNode)
            let resultTree = []

            // 2. 构建主流程树
            if (rootNode) {
                const builtRoot = this.buildTree(rootNode.id, nodeMap)
                if (builtRoot) {
                    resultTree.push(builtRoot)
                }
            }

            // 3. 找出所有被引用的节点 ID
            const referencedIds = this.getReferencedNodes(this.allNodes)

            // 4. 查找孤立节点
            this.allNodes.forEach(node => {
                const isRootNode = rootNode && node.id === rootNode.id
                const isReferenced = referencedIds.has(node.id)
                const isAlreadyInTree = resultTree.some(root => root.key === node.id)

                if (!isRootNode && !isReferenced && !isAlreadyInTree) {
                    const orphanTree = {
                        title: this.getNodeLabel(node),
                        key: node.id,
                        ...node,
                        children: []
                    }
                    resultTree.push(orphanTree)
                }
            })

            this.treeData = resultTree
        },

        // 递归构建树
        buildTree(nodeId, nodeMap, visited = new Set()) {
            const currentNode = nodeMap[nodeId]
            if (!currentNode || visited.has(nodeId)) {
                return null
            }

            visited.add(nodeId)

            const treeNode = {
                title: this.getNodeLabel(currentNode),
                key: currentNode.id,
                ...currentNode,
                children: []
            }

            // 查找子节点：通过parentNodeId查找
            const children = this.allNodes.filter(n => String(n.parentNodeId) === String(nodeId))

            if (children.length > 0) {
                children.forEach(child => {
                    const childTree = this.buildTree(child.id, nodeMap, visited)
                    if (childTree) {
                        treeNode.children.push(childTree)
                    }
                })
            }

            return treeNode
        },

        // 获取被引用的节点（用于查找孤立节点）
        getReferencedNodes(nodes) {
            const referencedIds = new Set()

            nodes.forEach(node => {
                // 父节点引用
                if (node.parentNodeId) {
                    referencedIds.add(node.parentNodeId)
                }

                // 跳转节点引用
                if (node.jumpNodeId) {
                    referencedIds.add(node.jumpNodeId)
                }
            })

            return referencedIds
        },

        // 刷新对话树
        refreshDialogueTree() {
            this.fetchDialogueTree()
        },

        // 获取节点显示标签
        getNodeLabel(node) {
            let label = `[${this.getNodeTypeText(node.nodeType)}] `

            if (node.nodeType === 'text' && node.content) {
                const firstContent = node.content.split('&')[0]
                label += firstContent.length > 20 ? firstContent.substring(0, 20) + '...' : firstContent
            } else if (node.content) {
                label += '媒体文件'
            }

            return label
        },

        // 根据ID获取节点标签
        getNodeLabelById(nodeId) {
            const node = this.allNodes.find(n => n.id === nodeId)
            return node ? this.getNodeLabel(node) : '未知节点'
        },

        // 获取节点类型文本
        getNodeTypeText(type) {
            const typeMap = {
                'text': '文本',
                'audio': '音频',
                'video': '视频',
                'phone': '电话'
            }
            return typeMap[type] || type
        },

        // 获取节点类型颜色
        getNodeTypeColor(type) {
            const colorMap = {
                'text': 'blue',
                'audio': 'cyan',
                'video': 'purple',
                'phone': 'red'
            }
            return colorMap[type] || 'blue'
        },

        // 树节点选择事件
        onTreeSelect(selectedKeys, info) {
            if (selectedKeys.length > 0 && info.node.dataRef) {
                this.selectNode(info.node.dataRef)
            } else {
                this.selectedNode = null
            }
        },

        // 选择节点
        selectNode(node) {
            this.selectedNode = node
        },

        // 关闭弹窗
        handleClose() {
            this.$emit('close')
        },

        // 添加节点
        handleAddNode() {
            this.nodeModal.mode = 'add'
            this.nodeModal.title = '添加节点'
            this.resetNodeForm()

            // 设置角色ID
            this.nodeForm.characterId = this.characterId
            this.nodeModal.visible = true
        },

        // 添加子节点
        handleAddChildNode(parentNode) {
            this.nodeModal.mode = 'add'
            this.nodeModal.title = '添加子节点'
            this.resetNodeForm()

            // 设置父节点
            this.nodeForm.characterId = this.characterId
            this.nodeForm.parentNodeId = parentNode.id
            this.nodeModal.visible = true
        },

        // 编辑节点
        handleEditNode(node) {
            this.nodeModal.mode = 'edit'
            this.nodeModal.title = '编辑节点'
            this.nodeForm = {
                id: node.id,
                characterId: node.characterId,
                parentNodeId: node.parentNodeId,
                nodeType: node.nodeType,
                content: node.content || '',
                triggerType: node.triggerType || 'click',
                jumpNodeId: node.jumpNodeId,
                startNode: node.startNode || false
            }
            this.nodeModal.visible = true
        },

        // 更新跳转节点
        async updateJumpNodeId(newJumpNodeId) {
            if (!this.selectedNode) {
                return this.$message.error('请先选择一个节点')
            }

            try {
                await updateDialogueNode({
                    id: this.selectedNode.id,
                    jumpNodeId: newJumpNodeId
                })

                this.$message.success('跳转节点更新成功')
                this.fetchDialogueTree()
            } catch (error) {
                console.error('更新跳转节点失败:', error)
                this.$message.error('更新失败')
            }
        },

        // 删除节点
        handleDeleteNode(node) {
            this.$confirm({
                title: '警告',
                content: `确定删除节点 "${this.getNodeLabel(node)}" 吗？`,
                okText: '确定',
                okType: 'danger',
                cancelText: '取消',
                onOk: async () => {
                    try {
                        await deleteDialogueNode(node.id)
                        this.$message.success('删除成功')
                        this.fetchDialogueTree()
                        this.selectedNode = null
                    } catch (error) {
                        console.error('删除节点失败:', error)
                        this.$message.error('删除失败')
                    }
                }
            })
        },

        // 节点类型变化
        handleNodeTypeChange(type) {
            if (type === 'start') {
                this.nodeForm.startNode = true
            } else {
                this.nodeForm.startNode = false
            }
        },

        // 获取媒体文件接受类型
        getMediaAccept() {
            switch (this.nodeForm.nodeType) {
                case 'audio':
                    return '.mp3,.wav,.ogg,.m4a'
                case 'video':
                case 'phone':
                    return '.mp4,.avi,.mov,.wmv,.mkv'
                default:
                    return '*'
            }
        },

        // 获取媒体上传提示
        getMediaTip() {
            switch (this.nodeForm.nodeType) {
                case 'audio':
                    return '支持 mp3, wav, ogg, m4a 格式，建议大小不超过 10MB'
                case 'video':
                case 'phone':
                    return '支持 mp4, avi, mov, wmv, mkv 格式，建议大小不超过 50MB'
                default:
                    return '请上传文件'
            }
        },

        // 媒体上传前检查
        beforeMediaUpload(file) {
            let isValid = true
            let message = ''

            if (this.nodeForm.nodeType === 'audio') {
                const isAudio = /\.(mp3|wav|ogg|m4a)$/i.test(file.name)
                const isLt10M = file.size / 1024 / 1024 < 10
                isValid = isAudio && isLt10M
                message = !isAudio ? '只能上传音频文件!' : '文件大小不能超过 10MB!'
            } else if (this.nodeForm.nodeType === 'video' || this.nodeForm.nodeType === 'phone') {
                const isVideo = /\.(mp4|avi|mov|wmv|mkv)$/i.test(file.name)
                const isLt50M = file.size / 1024 / 1024 < 50
                isValid = isVideo && isLt50M
                message = !isVideo ? '只能上传视频文件!' : '文件大小不能超过 50MB!'
            }

            if (!isValid) {
                this.$message.error(message)
                return false
            }

            return true
        },

        // 媒体上传处理
        async handleMediaUpload({ file, onSuccess, onError }) {
            try {
                // 这里应该是实际的上传API调用
                const mockUrl = URL.createObjectURL(file)

                // 模拟API延迟
                setTimeout(() => {
                    this.nodeForm.content = mockUrl
                    onSuccess({ url: mockUrl }, file)
                    this.$message.success('上传成功')
                }, 1000)
            } catch (error) {
                onError(error)
                this.$message.error('上传失败')
            }
        },

        // 预览媒体
        previewMedia(url, type) {
            this.previewModal = {
                visible: true,
                title: type === 'video' ? '视频预览' : '音频预览',
                url: url,
                type: type
            }
        },

        // 重置节点表单
        resetNodeForm() {
            this.nodeForm = {
                id: null,
                characterId: null,
                parentNodeId: null,
                nodeType: 'text',
                content: '',
                triggerType: 'click',
                jumpNodeId: null,
                startNode: false
            }

            if (this.$refs.nodeForm) {
                this.$refs.nodeForm.resetFields()
            }
        },

        // 提交节点表单
        submitNodeForm() {
            this.$refs.nodeForm.validate(async (valid) => {
                if (!valid) return

                this.nodeModal.loading = true
                try {
                    if (this.nodeModal.mode === 'add') {
                        await addDialogueNode(this.nodeForm)
                        this.$message.success('添加成功')
                    } else {
                        await updateDialogueNode(this.nodeForm)
                        this.$message.success('更新成功')
                    }

                    this.nodeModal.visible = false
                    this.fetchDialogueTree()

                    // 如果是编辑当前选中的节点，刷新选中状态
                    if (this.selectedNode && this.selectedNode.id === this.nodeForm.id) {
                        this.selectNode({ ...this.selectedNode, ...this.nodeForm })
                    }
                } catch (error) {
                    console.error('保存节点失败:', error)
                    this.$message.error(error.message || '保存失败')
                } finally {
                    this.nodeModal.loading = false
                }
            })
        },

        // 节点弹窗取消
        handleNodeModalCancel() {
            this.nodeModal.visible = false
            this.resetNodeForm()
        }
    }
}
</script>

<style scoped>
.dialogue-modal>>>.ant-modal-body {
    height: 80vh;
    overflow: hidden;
}

.dialogue-editor {
    height: 100%;
}

/* 树节点选中样式 */
.selected-node-tree {
    background-color: #e6f7ff;
    border-left: 3px solid #1890ff;
    padding: 4px 8px;
    margin: -4px -8px;
    display: block;
}

/* 覆盖 AntD 默认选中样式 */
:deep(.ant-tree-node-content-wrapper.ant-tree-node-selected) {
    background-color: transparent !important;
}
</style>